import { BaseKeyProvider } from 'livekit-client';

// MatrixRTCSessionEvent enum values - we use strings since we can't guarantee the import
const ENCRYPTION_KEY_CHANGED = 'encryption_key_changed';

/**
 * MatrixKeyProvider - Provides E2EE keys from MatrixRTC session to LiveKit
 * This allows the observer to decrypt E2EE-encrypted media streams
 */
export class MatrixKeyProvider extends BaseKeyProvider {
  constructor() {
    super({ ratchetWindowSize: 10, keyringSize: 256 });
    this.rtcSession = null;
    this.onEncryptionKeyChanged = this.onEncryptionKeyChanged.bind(this);
  }

  setRTCSession(rtcSession) {
    // Remove listener from old session
    if (this.rtcSession) {
      this.rtcSession.off(ENCRYPTION_KEY_CHANGED, this.onEncryptionKeyChanged);
    }

    this.rtcSession = rtcSession;

    if (this.rtcSession) {
      console.log('[E2EE] Setting up encryption key listener on MatrixRTC session');
      console.log('[E2EE] Session memberships:', this.rtcSession.memberships?.length || 0);
      console.log('[E2EE] Session memberships details:', this.rtcSession.memberships?.map(m => ({
        sender: m.sender,
        deviceId: m.deviceId,
      })));

      // Listen for encryption key changes
      this.rtcSession.on(ENCRYPTION_KEY_CHANGED, this.onEncryptionKeyChanged);

      // Also listen for all events to debug
      const allEvents = ['memberships_changed', 'encryption_key_changed', 'call_encryption_keys_received'];
      allEvents.forEach(eventName => {
        this.rtcSession.on(eventName, (...args) => {
          console.log(`[E2EE] Event '${eventName}' received:`, args);
        });
      });

      // Re-emit any existing keys after a small delay to ensure session is ready
      if (typeof this.rtcSession.reemitEncryptionKeys === 'function') {
        console.log('[E2EE] Will request re-emit of existing encryption keys');
        // Immediate call
        this.rtcSession.reemitEncryptionKeys();
        // Also try after a delay in case session needs time to sync
        setTimeout(() => {
          console.log('[E2EE] Delayed re-emit request');
          if (this.rtcSession?.reemitEncryptionKeys) {
            this.rtcSession.reemitEncryptionKeys();
          }
        }, 2000);
        setTimeout(() => {
          console.log('[E2EE] Second delayed re-emit request');
          if (this.rtcSession?.reemitEncryptionKeys) {
            this.rtcSession.reemitEncryptionKeys();
          }
        }, 5000);
      }
    }
  }

  async onEncryptionKeyChanged(encryptionKey, encryptionKeyIndex, participantId) {
    console.log(`[E2EE] Received encryption key for participant=${participantId} index=${encryptionKeyIndex}`);

    try {
      // Import the raw key for HKDF derivation
      const keyMaterial = await crypto.subtle.importKey(
        'raw',
        encryptionKey,
        'HKDF',
        false,
        ['deriveBits', 'deriveKey']
      );

      // Set the key for this participant
      this.onSetEncryptionKey(keyMaterial, participantId, encryptionKeyIndex);

      console.log(
        `[E2EE] Successfully set encryption key for participant=${participantId} index=${encryptionKeyIndex}`
      );
    } catch (e) {
      console.error(
        `[E2EE] Failed to import key for participant=${participantId}:`,
        e
      );
    }
  }

  cleanup() {
    if (this.rtcSession) {
      this.rtcSession.off(ENCRYPTION_KEY_CHANGED, this.onEncryptionKeyChanged);
      this.rtcSession = null;
    }
  }
}
